
import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

type Organization = {
  id: string;
  name: string;
};

type OrganizationContextType = {
  currentOrganization: Organization | null;
  setCurrentOrganization: (organization: Organization | null) => void;
  organizations: Organization[];
  isLoading: boolean;
};

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { subscriptionData } = useAuth();

  useEffect(() => {
    async function fetchOrganizations() {
      try {
        let query = supabase
          .from("organizations")
          .select("id, name")
          .order("name");

        // Filtrar por subscription_id se fornecido
        if (subscriptionData.subscription_id) {
          query = query.eq("subscription_id", subscriptionData.subscription_id);
        }

        const { data, error } = await query;

        if (error) {
          console.error("Error fetching organizations:", error);
          return;
        }

        const orgsData: Organization[] = (data || []).map(org => ({
          id: org.id,
          name: org.name || ""
        }));
        
        setOrganizations(orgsData);
        
        // Select first organization if none is selected
        if (orgsData.length > 0 && !currentOrganization) {
          setCurrentOrganization(orgsData[0]);
        }
      } catch (error) {
        console.error("Failed to fetch organizations:", error);
      } finally {
        setIsLoading(false);
      }
    }

    if (subscriptionData.subscription_id) {
      fetchOrganizations();
    }
  }, [subscriptionData.subscription_id]);

  return (
    <OrganizationContext.Provider 
      value={{ 
        currentOrganization, 
        setCurrentOrganization, 
        organizations,
        isLoading
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  
  return context;
}
